/*eslint max-classes-per-file: "off" */

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MonoTypeOperatorFunction, Observable, Subject } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { PagedList } from "../modules/shared/models/api-response.model";
import { buildExportFilename } from "../modules/shared/utils/export-helpers";
import { AuthService } from "./auth.service";

export class AccountContactRole {
    accountContactRoleId: number;
    name: string;
    createdAt: Date;
    updatedAt: Date;

    static fromJSON(obj): AccountContactRole {
        const temp = new AccountContactRole();
        Object.assign(temp, obj);
        if (obj.createdAt) {
            temp.createdAt = new Date(obj.createdAt);
        }
        if (obj.updatedAt) {
            temp.updatedAt = new Date(obj.updatedAt);
        }

        return temp;
    }
}

export class AccountContact {
    accountContactId: number;
    accountId: number;
    userId: number;
    accountContactRoleId: number;
    accountContactRoleName: string;
    accountContactRoleDescription: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    jobTitle: string;
    displayOrder: number;
    isDisplayed: boolean;
    createdAt: Date;
    updatedAt: Date;

    static fromJSON(obj): AccountContact {
        const temp = new AccountContact();
        Object.assign(temp, obj);
        if (obj.createdAt) {
            temp.createdAt = new Date(obj.createdAt);
        }
        if (obj.updatedAt) {
            temp.updatedAt = new Date(obj.updatedAt);
        }

        return temp;
    }
}

export type AccountFee = {
    feeId: string;
    accountId: string;
    paymentType: string;
    startDate: string;
    frequency: string;
    departmentTypeCodes: string[];
    coverageTypeCodes: string[];
    loanClassificationCodes: string[];
    itemCount: number;
    rate: number;
    amountType: string;
    minimum: number;
    maximum: number;
    actionType: string;
    paymentMethod: string;
    total: number;
    isNew: boolean;
    isArchived: boolean;
    lastFrequencyUpdate: string[];
    paymentTiming: string;
    invoiceGroupId?: string;
    details?: string;
    expiresAfter?: string;
};

const API_URL = environment.api;

export type Account = {
    accountId: string;
    claimPaymentAddress: string;
    claimPaymentMethod: string;
    collateralTypes: string;
    creditUnionFlag: string;
    departmentTypes: string;
    emailMfaEnabled: boolean;
    invoiceCreatedBy: string;
    invoiceType: string;
    isActive: boolean;
    isDeleted: boolean;
    mailingAddress1: string;
    mailingAddress2: string;
    mailingCity: string;
    mailingState: string;
    mailingZip: string;
    name: string;
    paymentMethod: string;
    phone: string;
    priority: number;
    productLines: string;
    programTypes: string;
    showClaims: boolean;
    showServices: boolean;
    showOutstandingPremium: boolean;
    streetAddress1: string;
    streetAddress2: string;
    streetCity: string;
    streetState: string;
    streetZip: string;
    trimmedAccountId: string;
    lastDataReceived: string;
    status: "Cancelled" | "In Programming" | "Live" | "Pending";
    statusDate: string;
    firstProcessCycle: string;
    subsiteUrl: string;
    doNotSendSmsBefore: string | null;
    loanFileFrequency: number | null;
};

export type AccountPreview = Pick<
    Account,
    | "accountId"
    | "collateralTypes"
    | "departmentTypes"
    | "firstProcessCycle"
    | "isActive"
    | "isDeleted"
    | "lastDataReceived"
    | "loanFileFrequency"
    | "name"
    | "programTypes"
    | "showClaims"
    | "showServices"
    | "status"
    | "statusDate"
    | "trimmedAccountId"
>;

export type AccountClaimType = {
    claimTypeCode: string;
    name: string;
    description?: string;
};

/** Labelled "Service Type" in the UI. */
export type AccountServiceType = {
    serviceTypeCode: string;
    name: string;
    description?: string;
    lenderCanInitiate: boolean;
    sortOrder: number;
    isActive: boolean;
};

export type FeeOption = {
    fieldName: string;
    value: string;
};

export type AccountDetail = {
    paymentMethod: string;
    claimPaymentMethod: string;
    claimPaymentAddress: string;
    invoiceCreatedBy: string;
    invoiceType: string;
    showClaims: string;
    showServices: string;
    showOutstandingPremium: string;
    emailMfaEnabled: string;
    subsiteUrl: string;
    doNotSendSmsBefore: string | null;
};

@Injectable()
export class AccountService {
    pipe(arg0: MonoTypeOperatorFunction<unknown>) {
        throw new Error("Method not implemented.");
    }
    accountUpdated = new Subject<string>();

    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {}

    // TODO: This probably more properly belongs in a filter rather than http service
    //       (e.g. { { claimType | description: 'claimType' } })
    private claimTypeTips = [
        {
            name: "Bankruptcy",
            description:
                "Prevented from taking possession of the collateral or after repossession of the collateral, damage was discovered."
        },
        {
            name: "Non-Repossesion Damage",
            description: "Non-repossession claim with borrower’s involvement."
        },
        { name: "Theft", description: "Unrecovered collateral." },
        {
            name: "Conversion",
            description:
                "Borrower sells, trades or disposes of the entire collateral without your consent or when the Borrower and the collateral cannot be located."
        },
        {
            name: "Secretion", // Exact duplicate of Conversion
            description:
                "Borrower sells, trades or disposes of the entire collateral without your consent or when the Borrower and the collateral cannot be located."
        },
        {
            name: "Skip", // Exact duplicate of Conversion
            description:
                "Borrower sells, trades or disposes of the entire collateral without your consent or when the Borrower and the collateral cannot be located."
        },
        {
            name: "Confiscation",
            description:
                "Appropriation, condemnation or seizure of collateral by a public official."
        },
        {
            name: "Premium Deficiency-Coverage A",
            description: "Loan paid in full other than remaining CPI premium."
        },
        {
            name: "Premium Deficiency-Coverage B",
            description:
                "After repossession and selling the collateral there is still a balance."
        },
        {
            name: "Repossession Expense Reimbursement",
            description:
                "Reimbursement of Repossession, Towing, Storage, and Mechanical Lien Expenses."
        },
        {
            name: "Repossession Property Coverage",
            description:
                "Collateral was involved in an accident after the date of repossession and while in the custody of the lender."
        },
        {
            name: "Instrument Non-Filing/Error & Omissions",
            description:
                "Prevented from obtaining possession, retaining proceeds or enforcing your right of the collateral due to a title issue."
        },
        {
            name: "Repossession Damage",
            description: "Collateral acquired damages prior to repossession."
        },
        {
            name: "Non-Repossession Damage",
            description: "Non-repossession claim with borrower’s involvement"
        },
        {
            name: "Primary Deductible",
            description:
                "Reimbursement of the difference between the borrower’s primary insurance deductible and your maximum allowed deductible."
        },
        {
            name: "Lienholder & Loss Payee Error & Omissions",
            description:
                "A claim payment was made directly to the borrower by their primary insurance due to missing lienholder or loss payee information on the borrower’s primary policy."
        }
    ];

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    getAll(
        offset = 0,
        limit = 20,
        sort: string | null = "+name",
        search: string | null = null,
        accountStatus: string[] | null = null
    ): Observable<PagedList<AccountPreview>> {
        let p = {};

        if (limit !== -1) {
            p = {
                offset: `${offset * limit}`,
                limit: `${limit}`
            };
        }

        if (search) {
            p["filter"] = search;
        }

        if (accountStatus && accountStatus.length > 0) {
            p["account-status"] = accountStatus.join(",");
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        } else {
            sort = "+name";
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        const options = { headers: this._headers, params: p };
        return this.http
            .get<
                PagedList<AccountPreview>
            >(`${API_URL}/api/v1/accounts`, options)
            .pipe(
                map((res) => {
                    res.offset = res.offset / limit;
                    return res;
                })
            );
    }

    exportAll(
        columns,
        format,
        sort: string | null = null,
        search: string | null = null,
        accountStatus: string[] | null = null
    ): Observable<Blob> {
        const p = {
            format: format
        };

        if (search) {
            p["filter"] = search;
        }

        if (accountStatus && accountStatus.length > 0) {
            p["account-status"] = accountStatus.join(",");
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            p["sort"] = sort;
        }

        const options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };
        return this.http
            .post<Blob>(`${API_URL}/api/v1/accounts/export`, columns, options)
            .pipe(
                map((res) => {
                    const url = window.URL.createObjectURL(res);
                    const a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        "accounts_export",
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getAccounts(ids: string) {
        const stringExpanderFn = (val: string) => {
            if (!val) {
                return;
            }

            return val.replace(/,/g, ", ");
        };

        return this.http
            .get<
                Account[]
            >(`${API_URL}/api/v1/accounts/ids?accountIds=${ids}`, this._defaultOptions)
            .pipe(
                map((res) => {
                    res.forEach((account) => {
                        account.departmentTypes = stringExpanderFn(
                            account.departmentTypes
                        );
                        account.collateralTypes = stringExpanderFn(
                            account.collateralTypes
                        );
                        account.programTypes = stringExpanderFn(
                            account.programTypes
                        );
                        account.productLines = stringExpanderFn(
                            account.productLines
                        );
                    });

                    return res;
                })
            );
    }

    getAccountClaimTypes(id: string) {
        return this.http
            .get<
                AccountClaimType[]
            >(`${API_URL}/api/v1/accounts/${id}/claim-types`, this._defaultOptions)
            .pipe(
                map((res) => {
                    res.forEach((elem) => {
                        const temp = this.claimTypeTips.find(
                            (x) => x.name === elem.name
                        );
                        elem.description =
                            temp ? temp.description : "No Current Description";
                    });
                    return res;
                })
            );
    }

    getAccountUnassignedClaimTypes(id: string) {
        return this.http.get<AccountClaimType[]>(
            `${API_URL}/api/v1/accounts/${id}/claim-types/unassigned`,
            this._defaultOptions
        );
    }

    // Service (assignment) types. Unlike claim types, descriptions come from the API keyed by
    // code rather than a hardcoded lookup matched on name.
    getAccountServiceTypes(id: string) {
        return this.http.get<AccountServiceType[]>(
            `${API_URL}/api/v1/accounts/${id}/service-types`,
            this._defaultOptions
        );
    }

    getAccountUnassignedServiceTypes(id: string) {
        return this.http.get<AccountServiceType[]>(
            `${API_URL}/api/v1/accounts/${id}/service-types/unassigned`,
            this._defaultOptions
        );
    }

    getISIContacts(): Observable<AccountContact[]> {
        const stringList = this._authService.claims.accountIds.toString();
        const ids: string[] = stringList.split(",");
        return this.getAccountContacts(ids);
    }

    getAccountContactRoles(): Observable<AccountContactRole[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/account-contact-roles`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: unknown[]) => {
                    return res.map(AccountContactRole.fromJSON);
                })
            );
    }

    getAccountContacts(
        accountIds: string[] | null = null
    ): Observable<AccountContact[]> {
        const p = {};

        if (accountIds) {
            p["accountIds"] = accountIds;
        }

        const options = { headers: this._headers, params: p };
        return this.http
            .get(`${API_URL}/api/v1/account-contacts`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: unknown[]) => {
                    return res.map(AccountContact.fromJSON);
                })
            );
    }

    saveAccountContacts(accountId, obj) {
        return this.http
            .post(
                `${API_URL}/api/v1/accounts/${accountId}/contacts`,
                obj,
                this._defaultOptions
            )
            .pipe(
                shareReplay(),

                tap((_) => this.accountUpdated.next("updated"))
            );
    }

    getFeeOptions(fieldName: string) {
        return this.http
            .get<
                FeeOption[]
            >(`${API_URL}/api/v1/account/fee-options/${fieldName}`, this._defaultOptions)
            .pipe(shareReplay());
    }

    getAccountFees(accountId: string) {
        return this.http.get<AccountFee[]>(
            `${API_URL}/api/v1/account/fees/${accountId}`,
            this._defaultOptions
        );
    }

    getFeeItemCount(
        accountId: string,
        departments: string[] | null,
        coverageTypes: string[] | null,
        classifications: string[] | null
    ) {
        const p = {
            accountId: accountId
        };

        if (departments && departments.length > 0) {
            p["departmentTypeCodes"] = departments.join(",");
        }

        if (classifications && classifications.length > 0) {
            const c = classifications.filter((c) => c != null);
            p["loanClassificationCodes"] = c.join(",");
        }

        if (coverageTypes && coverageTypes.length > 0) {
            p["coverageTypeCodes"] = coverageTypes.join(",");
        }

        const options = { headers: this._headers, params: p };
        return this.http.get<number>(
            `${API_URL}/api/v1/account/fees/item-count`,
            options
        );
    }

    saveAccountFees(accountId, obj) {
        return this.http
            .post(
                `${API_URL}/api/v1/account/fees/${accountId}`,
                obj,
                this._defaultOptions
            )
            .pipe(
                shareReplay(),
                tap((_) => this.accountUpdated.next("updated"))
            );
    }

    saveAccountClaimTypes(accountId, claimTypes: string[]) {
        return this.http
            .post(
                `${API_URL}/api/v1/accounts/${accountId}/claim-types`,
                claimTypes,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    saveAccountServiceTypes(accountId, serviceTypes: string[]) {
        return this.http
            .post(
                `${API_URL}/api/v1/accounts/${accountId}/service-types`,
                serviceTypes,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    toggleIsDeleted(accountId, newVal) {
        return this.http
            .put(
                `${API_URL}/api/v1/accounts/${accountId}/deleted`,
                newVal,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }

    // TODO: Can't really type this form until account-edit.page switches to Typed Reactive Forms
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    saveAccountDetails(accountId: string, data: AccountDetail) {
        console.log(data);
        return this.http
            .post(
                `${API_URL}/api/v1/accounts/${accountId}`,
                data,
                this._defaultOptions
            )
            .pipe(shareReplay());
    }
}
